import { PreferenceGroupSubTypeEnum } from 'api-hooks/preference-tag';
import { useFormState } from 'components/elements/form';
import deleteColumn from 'components/elements/table/editable-columns/delete-column';
import preferenceColumn from 'components/elements/table/editable-columns/preference-column';
import TableInput from 'components/elements/table/table-input';
import {
  ActionType,
  ColumnDataType,
  IColumn,
} from 'components/elements/table/type';
import useGetAirlineColumn from 'hooks/use-get-airline-column';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { TourFlightAirlineFormType, TourListFormType } from '../form-type';
import { onAddNewAirline } from '../hooks/use-get-default-values';

interface Props {
  parentIndex: number;
}

function useGetAirlineTableList(): IColumn<TourFlightAirlineFormType>[] {
  const { t } = useTranslation();
  const { editable } = useFormState();
  const airlineColumn = useGetAirlineColumn();
  return [
    {
      header: t('common:airline'),
      accessorKey: 'airlineId',
      minSize: 100,
      dataType: ColumnDataType.select,
      required: true,

      selectProps: airlineColumn,
    },
    {
      header: t('common:date'),
      accessorKey: 'date',
      minSize: 100,
      dataType: ColumnDataType.date,
      required: true,
    },
    {
      header: t('common:detail'),
      accessorKey: 'detail',
      minSize: 100,
      dataType: ColumnDataType.text,
      required: true,
    },
    {
      header: t('common:class'),
      accessorKey: 'preferenceId',
      minSize: 100,
      dataType: ColumnDataType.select,
      selectProps: preferenceColumn({
        subType: PreferenceGroupSubTypeEnum.FlightClass,
      }),
      selectGetLastItem: true,
    },
    {
      header: t('common:route'),
      accessorKey: 'route',
      minSize: 100,
      dataType: ColumnDataType.text,
      required: true,
    },
    {
      header: t('common:time'),
      accessorKey: 'time',
      minSize: 100,
      dataType: ColumnDataType.text,
      required: true,
    },

    ...deleteColumn(editable),
  ];
}

export default function AirlineTable(props: Props) {
  const { editable } = useFormState();
  const { trigger, getValues, control, clearErrors } =
    useFormContext<TourListFormType>();

  const name = `tourFlights.${props.parentIndex}.tourFlightAirlines` as const;
  const columns = useGetAirlineTableList();

  const { fields, append, remove, update } = useFieldArray({
    name,
    control,
    keyName: '__tempId',
  });

  const dispatch = React.useCallback(
    (action) => {
      const airlines = getValues(name);
      const columnId = action.columnId as keyof TourFlightAirlineFormType;
      const value = action.value;
      const currentRowIndex = action.rowIndex as number;
      const airline = airlines[currentRowIndex];
      const type = action.type as ActionType;

      if (type === 'addRow') {
        append(onAddNewAirline());
        trigger(name);
      } else if (type === 'removeRow') {
        remove(currentRowIndex);
        trigger(name);
      } else if (type === 'update_cell') {
        update(currentRowIndex, {
          ...airline,
          [columnId]: value,
        });
        clearErrors(`${name}.${currentRowIndex}`);
      }
    },
    [append, clearErrors, getValues, name, remove, trigger, update],
  );

  return (
    <div style={{ width: '100' }}>
      <TableInput
        columns={columns}
        data={fields}
        dispatch={dispatch}
        disabled={!editable}
        name={name}
        uniqueRowKey="customId"
      />
    </div>
  );
}
